import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  AddCircleOutlined,
  List as ListIcon,
  Assessment,
  MoreHoriz,
  Brightness4,
  Brightness7,
  SettingsBrightness,
  Info,
  Refresh,
  FilterList,
  Settings as SettingsIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { Badge } from "@mui/material";
import { usePendingAttachments } from "../hooks/useAttachments";
import { useThemeMode } from "../theme/ThemeContext";
import { AboutDialog } from "./AboutDialog";
import { useConfig } from "../api/queries";
import { type PredefinedFilterDto } from "../api/types";
import { buildPredefinedFilterUrl } from "../logic/predefined-filter-url";

export const MobileNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useThemeMode();
  const { data: config } = useConfig();
  const { data: pendingAttachments } = usePendingAttachments();
  const pendingCount = pendingAttachments?.length ?? 0;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const open = Boolean(anchorEl);

  const getValue = (path: string) => {
    if (anchorEl) return 3; // Highlight "More" if menu is open
    if (path === "/" || path.startsWith("/home")) return 0;
    if (path.startsWith("/transactions")) return 1;
    if (path.startsWith("/summary")) return 2;
    return 3; // "More" or any other page
  };

  const value = getValue(location.pathname);

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handlePredefinedFilter = (filter: PredefinedFilterDto) => {
    navigate(buildPredefinedFilterUrl(filter));
    handleClose();
  };

  const handleModeToggle = () => {
    if (mode === "light") setMode("dark");
    else if (mode === "dark") setMode("system");
    else setMode("light");
  };

  const getThemeIcon = () => {
    if (mode === "light") return <Brightness7 fontSize="small" />;
    if (mode === "dark") return <Brightness4 fontSize="small" />;
    return <SettingsBrightness fontSize="small" />;
  };

  const getThemeLabel = () => {
    if (mode === "light") return "Light Mode";
    if (mode === "dark") return "Dark Mode";
    return "System Theme";
  };

  return (
    <>
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          paddingBottom: "env(safe-area-inset-bottom)",
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
        elevation={3}
      >
        <BottomNavigation
          showLabels
          value={value}
          onChange={(_, newValue) => {
            // For the "More" tab (index 3), we don't auto-navigate here.
            if (newValue !== 3) {
              if (newValue === 0) navigate("/");
              if (newValue === 1) navigate("/transactions");
              if (newValue === 2) navigate("/summary");
            }
          }}
        >
          <BottomNavigationAction
            label="Home"
            icon={
              <Badge badgeContent={pendingCount} color="primary">
                <HomeIcon />
              </Badge>
            }
          />
          <BottomNavigationAction label="List" icon={<ListIcon />} />
          <BottomNavigationAction label="Summary" icon={<Assessment />} />
          <BottomNavigationAction
            label="More"
            icon={<MoreHoriz />}
            onClick={handleMoreClick}
          />
        </BottomNavigation>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top", // Opens upwards from the bottom nav
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              // Ensure it doesn't get hidden behind safe area if opened
              marginBottom: "10px",
            },
          },
        }}
      >
        <MenuItem onClick={() => handleNavigation("/add")}>
          <ListItemIcon>
            <AddCircleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Transaction</ListItemText>
        </MenuItem>

        {config?.predefinedFilters.map((filter) => (
          <MenuItem
            key={filter.name}
            onClick={() => handlePredefinedFilter(filter)}
          >
            <ListItemIcon>
              <FilterList fontSize="small" />
            </ListItemIcon>
            <ListItemText>{filter.name}</ListItemText>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem onClick={() => handleNavigation("/admin")}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Administration</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            setAboutOpen(true);
            handleClose();
          }}
        >
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>About</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            window.location.reload();
            handleClose();
          }}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Refresh</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleModeToggle}>
          <ListItemIcon>{getThemeIcon()}</ListItemIcon>
          <ListItemText>{getThemeLabel()}</ListItemText>
        </MenuItem>
      </Menu>
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
};
